import React from 'react';
import { Mail, Clock, Globe } from 'lucide-react';
import { SiteHeader } from '../components/site-header';
import { SiteFooter } from '../components/site-footer';

const WHATSAPP_NUMBER = '5491140967673';
const WHATSAPP_DISPLAY = '+54 9 11 4096-7673';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  'Hola JugarHoy, necesito ayuda con...',
)}`;
const SUPPORT_EMAIL = 'soporte@jugarhoy.com';

const WhatsAppGlyph: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.5-.515 2.793-1.39.13-.43.13-.802.058-1.146-.144-.487-3.184-1.832-3.385-1.832zM16 .5a15.06 15.06 0 0 0-15.063 15.064c0 2.522.616 5.018 1.794 7.215L.93 30.5l7.93-1.81a14.987 14.987 0 0 0 7.144 1.81C24.288 30.5 31 23.794 31 15.498A14.96 14.96 0 0 0 26.595 4.9 14.97 14.97 0 0 0 16 .5zm0 27.348a12.299 12.299 0 0 1-6.273-1.71l-.444-.273-4.66 1.064 1.087-4.476-.286-.46A12.398 12.398 0 0 1 3.624 15.6 12.39 12.39 0 0 1 16 3.235a12.4 12.4 0 0 1 12.376 12.388A12.41 12.41 0 0 1 16 27.848z" />
  </svg>
);

const SupportPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col bg-[#E0EAF3]">
      <SiteHeader />

      <main className="flex-1 py-10 md:py-16 px-4">
        <div className="container max-w-3xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold font-display text-[#1E3A5F] mb-3">
              Soporte y Contacto
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Estamos para ayudarte. Escribinos por WhatsApp y te respondemos lo antes posible.
            </p>
          </div>

          {/* WhatsApp CTA card */}
          <div className="bg-white rounded-2xl shadow-md p-6 md:p-8 text-center mb-6 border">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold rounded-full px-6 py-3 shadow-md hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <WhatsAppGlyph size={28} />
              <span className="text-base sm:text-lg">Hablar por WhatsApp</span>
            </a>
            <p className="mt-4 text-sm text-gray-600">{WHATSAPP_DISPLAY}</p>
          </div>

          {/* Other contacts */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border">
            <h2 className="text-lg font-bold font-display mb-4 text-[#1E3A5F]">
              Otras vías de contacto
            </h2>
            <ul className="space-y-3 text-sm sm:text-base">
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold">Correo electrónico: </span>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-[#EA580C] hover:underline"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold">Sitio web: </span>
                  <a
                    href="https://jugarhoy.com"
                    className="text-[#EA580C] hover:underline"
                  >
                    https://jugarhoy.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold">Horario de atención: </span>
                  Lunes a Viernes, 9:00 a 18:00 (ART)
                </div>
              </li>
            </ul>
          </div>

          <p className="text-xs sm:text-sm text-gray-500 text-center mt-8">
            ¿Sos cliente de un club? Tu administrador también puede ayudarte directamente desde la app.
          </p>
        </div>
      </main>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactanos por WhatsApp"
        className="fixed right-5 bottom-5 sm:right-6 sm:bottom-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl ring-1 ring-black/5 hover:bg-[#1ebe5d] hover:scale-105 transition-all"
      >
        <WhatsAppGlyph size={28} />
      </a>

      <SiteFooter />
    </div>
  );
};

export default SupportPage;
